import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Phone, MessageCircle, CheckCircle2, ArrowRight } from "lucide-react";
import { Nav } from "../components/Nav";
import { Locations } from "../components/Locations";
import { Footer } from "../components/Footer";
import { StickyCTA } from "../components/StickyCTA";
import { Reveal, Overline } from "../components/Reveal";
import { SEO_PAGES } from "../data/seoPages";
import { PHONE_TEL, WHATSAPP_URL, PHONE_DISPLAY } from "../data/content";

const DOMAIN = "https://drsiddarthsingh.in";
const HOME_TITLE =
  "Dr. Siddarth Singh | Interventional Pulmonology, Critical Care & Sleep Medicine — Zirakpur & Panchkula";
const HOME_DESC =
  "Dr. Siddarth Singh (MBBS, MD, DM — AIIMS Bhubaneswar) is a leading Interventional Pulmonologist in Zirakpur & Panchkula offering EBUS, bronchoscopy, thoracoscopy, ICU critical care and sleep medicine at Amcare Hospital Zirakpur & IRCC Hospital Panchkula.";

const setMeta = (key, val, isProp = false) => {
  const sel = isProp ? `meta[property="${key}"]` : `meta[name="${key}"]`;
  let el = document.head.querySelector(sel);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(isProp ? "property" : "name", key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", val);
};

export default function SeoPage({ slug }) {
  const p = SEO_PAGES[slug];

  useEffect(() => {
    if (!p) return;
    document.title = p.metaTitle;
    setMeta("description", p.metaDescription);
    setMeta("og:title", p.metaTitle, true);
    setMeta("og:description", p.metaDescription, true);
    setMeta("og:url", `${DOMAIN}/${slug}`, true);
    const canonical = document.head.querySelector('link[rel="canonical"]');
    if (canonical) canonical.setAttribute("href", `${DOMAIN}/${slug}`);

    const ld = document.createElement("script");
    ld.type = "application/ld+json";
    ld.id = "seo-page-jsonld";
    ld.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "MedicalWebPage",
          name: p.metaTitle,
          description: p.metaDescription,
          url: `${DOMAIN}/${slug}`,
          about: { "@type": "MedicalSpecialty", name: p.specialty },
        },
        {
          "@type": "FAQPage",
          mainEntity: p.faqs.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        },
      ],
    });
    document.head.appendChild(ld);
    return () => {
      document.title = HOME_TITLE;
      setMeta("description", HOME_DESC);
      setMeta("og:title", HOME_TITLE, true);
      setMeta("og:description", HOME_DESC, true);
      setMeta("og:url", `${DOMAIN}/`, true);
      const c = document.head.querySelector('link[rel="canonical"]');
      if (c) c.setAttribute("href", `${DOMAIN}/`);
      document.getElementById("seo-page-jsonld")?.remove();
    };
  }, [p, slug]);

  if (!p) return null;

  return (
    <div className="grain bg-[#FAF8F3] text-stone-800 min-h-screen" data-testid={`seo-page-${slug}`}>
      <Nav />
      <main className="pt-28">
        <section className="relative py-16 sm:py-20 overflow-hidden">
          <div className="breath-orb absolute -top-32 -right-32 w-[26rem] h-[26rem] rounded-full bg-teal-500/10 blur-3xl pointer-events-none" />
          <div className="max-w-4xl mx-auto px-5 sm:px-8 relative z-10">
            <Reveal>
              <Overline>{p.overline}</Overline>
              <h1 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl font-semibold text-stone-800 leading-[1.12] tracking-tight">
                {p.h1}
              </h1>
              <p className="mt-6 text-base sm:text-lg text-stone-500 leading-relaxed">{p.intro}</p>
              <div className="mt-8 flex flex-wrap gap-4">
                <a href={PHONE_TEL} data-testid="seo-call-button"
                  className="flex items-center gap-3 px-7 py-3.5 rounded-full bg-[#0B5A4E] text-[#FAF8F3] font-semibold text-sm hover:bg-[#0D6B5C] transition-colors">
                  <Phone size={16} /> Call {PHONE_DISPLAY}
                </a>
                <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" data-testid="seo-whatsapp-button"
                  className="flex items-center gap-3 px-7 py-3.5 rounded-full border border-[#25D366]/50 text-[#25D366] font-semibold text-sm hover:bg-[#25D366]/10 transition-colors">
                  <MessageCircle size={16} /> WhatsApp for Appointment
                </a>
              </div>
            </Reveal>
            <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="seo-highlights">
              {p.highlights.map((h) => (
                <Reveal key={h}>
                  <div className="flex items-start gap-3 p-4 rounded-xl border border-[#E3DCCB] bg-white">
                    <CheckCircle2 size={18} className="text-teal-700 shrink-0 mt-0.5" />
                    <p className="text-sm text-stone-600 leading-relaxed">{h}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        <section className="py-10">
          <div className="max-w-4xl mx-auto px-5 sm:px-8 space-y-10">
            {p.sections.map((s) => (
              <Reveal key={s.h}>
                <article>
                  <h2 className="font-serif-display text-2xl sm:text-3xl font-semibold text-stone-800 leading-snug">{s.h}</h2>
                  <p className="mt-4 text-sm sm:text-base text-stone-500 leading-relaxed">{s.p}</p>
                </article>
              </Reveal>
            ))}
            <Reveal>
              <div className="flex flex-wrap gap-2 pt-2" data-testid="seo-conditions">
                {p.conditions.map((c) => (
                  <span key={c} className="px-4 py-2 rounded-full border border-teal-600/25 bg-teal-600/5 text-teal-800 text-xs font-medium">
                    {c}
                  </span>
                ))}
              </div>
            </Reveal>
          </div>
        </section>

        <section className="py-14 bg-[#F1EDE3]/60 border-y border-[#E3DCCB]/60">
          <div className="max-w-4xl mx-auto px-5 sm:px-8">
            <Reveal>
              <Overline>Common Questions</Overline>
              <div className="space-y-6 mt-6">
                {p.faqs.map((f, i) => (
                  <article key={f.q} className="p-6 rounded-2xl border border-[#E3DCCB] bg-white" data-testid={`seo-faq-${i + 1}`}>
                    <h3 className="font-serif-display text-lg sm:text-xl font-semibold text-stone-800">{f.q}</h3>
                    <p className="mt-3 text-sm sm:text-base text-stone-500 leading-relaxed">{f.a}</p>
                  </article>
                ))}
              </div>
            </Reveal>
          </div>
        </section>

        <section className="py-14">
          <div className="max-w-4xl mx-auto px-5 sm:px-8">
            <Reveal>
              <Overline>Explore More</Overline>
              <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-4">
                {p.related.map((r) => (
                  <Link key={r} to={`/${r}`} data-testid={`seo-related-${r}`}
                    className="group flex items-center justify-between gap-3 p-5 rounded-xl border border-[#E3DCCB] bg-white hover:border-teal-600/40 transition-colors">
                    <span className="text-sm font-medium text-stone-700 group-hover:text-teal-800">{SEO_PAGES[r].shortTitle}</span>
                    <ArrowRight size={15} className="text-teal-700 group-hover:translate-x-1 transition-transform" />
                  </Link>
                ))}
              </div>
            </Reveal>
          </div>
        </section>

        <Locations />
      </main>
      <Footer />
      <StickyCTA />
    </div>
  );
}
